import {
  AspectRatioVariant,
  BorderRadius,
  FilesPreview,
  FileType,
  Flex,
  IconButton,
  Modal,
  ModalProps,
  PreviewFile,
  Spacing,
  withModalConditionalRender,
} from '@billy/design-system'
import { ReactElement, useCallback, useMemo } from 'react'

import { useUserOrganization } from '@modules-deprecated/app/organization'
import { DocumentStaticActions, DocumentStaticActionType } from '@views/documents/elements/DocumentStaticActions'

import { getImageClassName } from '../../../../utils/getClassName'
import { useDocumentDownload } from '../../hooks/useDocumentDownload'
import { DmsDocument } from '../../types'
import { DocumentDetails } from './elements/DocumentDetails'
import * as Styled from './styles'
import { useDocumentPreviewUrl } from './useDocumentPreviewUrl'

interface DocumentPreviewModalProps extends ModalProps {
  document: DmsDocument
  onAction?: (action: DocumentStaticActionType) => void
  showDownloadButton?: boolean
  visibleActions: DocumentStaticActionType[]
}

const DocumentPreviewModalContent = ({
  id,
  document,
  onAction,
  showDownloadButton = true,
  visibleActions,
}: DocumentPreviewModalProps): ReactElement => {
  const { organization } = useUserOrganization()
  const { previewUrl } = useDocumentPreviewUrl(organization?.id ?? '', document.id, document.name)

  const asType = document.name.toLowerCase().endsWith('.pdf') ? FileType.Pdf : FileType.Image
  const { downloadDocument, isDownloading } = useDocumentDownload()

  const files: PreviewFile[] | undefined = useMemo(
    () => (previewUrl ? [{ src: previewUrl, srcZoom: previewUrl, srcDownload: previewUrl }] : undefined),
    [previewUrl],
  )

  const handleDownload = useCallback(() => {
    if (organization?.id) {
      downloadDocument(organization.id, document.id, document.name)
    }
  }, [downloadDocument, organization?.id, document.id, document.name])

  return (
    <Modal id={id} size="xl">
      <Modal.Header title="">
        <Flex flexGrow={1} />
        <Styled.HeaderActions>
          {showDownloadButton && (
            <IconButton icon="download" loading={isDownloading} size="sm" variant="text" onClick={handleDownload} />
          )}
          {onAction && <DocumentStaticActions onSelect={onAction} visibleActions={visibleActions} />}
        </Styled.HeaderActions>
      </Modal.Header>
      <Modal.Body>
        <Flex>
          <Styled.PreviewWrapper>
            {files && (
              <FilesPreview
                aspectRatio={AspectRatioVariant.billAttachment}
                asType={asType}
                borderRadius={BorderRadius.R12}
                className={getImageClassName()}
                files={files}
                fitToHeight
                isFloatingActionsView
                margin={Spacing.S16}
                withShadow
                zoomable
              />
            )}
          </Styled.PreviewWrapper>
          <DocumentDetails document={document} />
        </Flex>
      </Modal.Body>
    </Modal>
  )
}

export const DocumentPreviewModal = withModalConditionalRender(DocumentPreviewModalContent)
